import { useCallback, useEffect, useMemo, useState } from 'react';
import { Search, ExternalLink, Phone, Plus, Check } from 'lucide-react';
import AppDateField from '@/components/AppDateField';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { useApp } from '@/contexts/AppContext';
import { differenceInDays, format, parseISO, startOfDay } from 'date-fns';
import { toast } from 'sonner';
import {
  getWorkRightsPackByCategory,
  WORK_RIGHTS_PACKS,
} from '../../../ui-foundation/features/work';
import {
  AppButton,
  AppCard,
  AppCardBody,
  AppCardHeader,
  AppDisclosureGlyph,
  AppEmptyState,
  AppErrorState,
  AppFieldGroup,
  AppGuideCardButton,
  AppInlineAlert,
  AppInput,
  AppLoadingState,
  AppText,
  AppTextArea,
} from '../../../ui-foundation';
import { AppIconButton, AppNativeSelect, AppPanelButton } from '../../../ui-foundation/adapters/web';
import SectionAccordionCard from '@/components/SectionAccordionCard';

interface ContentCard {
  id: string;
  category: string;
  title: string;
  summary: string;
  body: string;
  icon: string;
  link_label: string | null;
  link_url: string | null;
  last_reviewed_at: string;
}

type WorkDeadlineType =
  | 'probation-end'
  | 'performance-review'
  | 'contract-renewal'
  | 'flexible-working-response'
  | 'redundancy-consultation'
  | 'other';

interface WorkDeadline {
  id: string;
  title: string;
  deadline_date: string;
  type: WorkDeadlineType;
  notes: string | null;
  completed: boolean;
  created_at: string;
}

type DeadlineSupport = {
  summary: string;
  linkLabel: string;
  linkUrl: string;
  helper: string;
  placeholder: string;
};

const CATEGORY_COLORS: Record<string, string> = {
  'Dismissal & Redundancy': 'bg-red-100 text-red-800',
  'Pay & Hours': 'bg-amber-100 text-amber-800',
  'Family & Leave': 'bg-green-100 text-green-800',
  'Discrimination & Rights at Work': 'bg-blue-100 text-blue-800',
};

const DEADLINE_TYPE_OPTIONS: Array<{ value: WorkDeadlineType; label: string }> = [
  { value: 'probation-end', label: 'Probation end' },
  { value: 'performance-review', label: 'Performance review' },
  { value: 'contract-renewal', label: 'Contract renewal' },
  { value: 'flexible-working-response', label: 'Flexible working response' },
  { value: 'redundancy-consultation', label: 'Redundancy consultation' },
  { value: 'other', label: 'Other work deadline' },
];

const DEADLINE_SUPPORT: Record<WorkDeadlineType, DeadlineSupport> = {
  'probation-end': {
    summary: 'Before probation ends, get the review date, expected standards, and any extension or confirmation outcome in writing.',
    linkLabel: 'Open Acas contract guidance',
    linkUrl: 'https://www.acas.org.uk/employment-contracts-and-the-law',
    helper: 'Use the date you expect the probation decision or review to be confirmed.',
    placeholder: 'Probation review meeting',
  },
  'performance-review': {
    summary: 'Keep the appraisal date visible so objectives, notes, and any follow-up actions do not get lost after the meeting.',
    linkLabel: 'Open Acas performance guidance',
    linkUrl: 'https://www.acas.org.uk/performance-management',
    helper: 'Use the date the review meeting or formal follow-up should happen.',
    placeholder: 'Quarterly performance review',
  },
  'contract-renewal': {
    summary: 'Track the date you need clarity on whether a fixed-term contract is being renewed, changed, or ended.',
    linkLabel: 'Open Acas fixed-term contract guidance',
    linkUrl: 'https://www.acas.org.uk/fixed-term-contracts',
    helper: 'Use the renewal decision date, not just the contract end date.',
    placeholder: 'Fixed-term contract renewal check',
  },
  'flexible-working-response': {
    summary: 'Keep the employer response date visible. GOV.UK says employers should decide within 2 months unless a longer period is agreed.',
    linkLabel: 'Open GOV.UK flexible working guidance',
    linkUrl: 'https://www.gov.uk/flexible-working/after-the-application',
    helper: 'Use the employer response deadline. Current GOV.UK guidance says this is usually within 2 months.',
    placeholder: 'Employer response to flexible working request',
  },
  'redundancy-consultation': {
    summary: 'Track consultation dates and the point where written next steps or notices should have been given.',
    linkLabel: 'Open Acas redundancy consultation guidance',
    linkUrl: 'https://www.acas.org.uk/collective-consultation-redundancy',
    helper: 'Use the next consultation meeting, consultation start, or promised written-outcome date.',
    placeholder: 'Redundancy consultation meeting',
  },
  other: {
    summary: 'Keep any legally or practically important work date visible here so it does not get buried in email or meeting notes.',
    linkLabel: 'Open Acas employment advice',
    linkUrl: 'https://www.acas.org.uk/',
    helper: 'Use this for any other date you need to keep visible.',
    placeholder: 'Important work deadline',
  },
};

function notifySectionRefresh() {
  window.dispatchEvent(new CustomEvent('adulting:data-changed'));
}

function getDaysBadge(dateStr: string) {
  const days = differenceInDays(parseISO(dateStr), startOfDay(new Date()));
  if (days < 0) return { text: `${Math.abs(days)}d overdue`, cls: 'bg-red-100 text-red-800' };
  if (days < 30) return { text: `${days}d`, cls: 'bg-red-100 text-red-800' };
  if (days < 60) return { text: `${days}d`, cls: 'bg-amber-100 text-amber-800' };
  return { text: `${days}d`, cls: 'bg-green-100 text-green-800' };
}

function getReminderWindow(dateStr: string) {
  const days = differenceInDays(parseISO(dateStr), startOfDay(new Date()));
  if (days < 0) return 'Missed';
  if (days <= 3) return '3-day window';
  if (days <= 14) return '2-week window';
  if (days <= 30) return '1-month window';
  return null;
}

function SelectField({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (value: string) => void;
  options: Array<{ value: string; label: string }>;
}) {
  return (
    <AppNativeSelect
      value={value}
      onChange={(event) => onChange(event.target.value)}
    >
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </AppNativeSelect>
  );
}

function MetricCard({
  label,
  value,
  tone = 'default',
}: {
  label: string;
  value: string;
  tone?: 'default' | 'warning';
}) {
  const valueClass = tone === 'warning' ? 'text-amber-700' : 'text-foreground';

  return (
    <div className="rounded-xl border border-border bg-background px-3 py-3">
      <p className="text-[10px] font-medium uppercase tracking-[0.08em] text-[#888]">{label}</p>
      <p className={cn('mt-1 text-sm font-semibold', valueClass)}>{value}</p>
    </div>
  );
}

export default function RightsTab() {
  const { user } = useApp();
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState<string | null>(null);
  const [cards, setCards] = useState<ContentCard[]>([]);
  const [deadlines, setDeadlines] = useState<WorkDeadline[]>([]);
  const [loading, setLoading] = useState(true);
  const [deadlineLoading, setDeadlineLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [deadlineError, setDeadlineError] = useState<string | null>(null);
  const [showAddDeadline, setShowAddDeadline] = useState(false);
  const [openSections, setOpenSections] = useState({
    deadlines: false,
    rights: false,
    help: false,
  });
  const [dlTitle, setDlTitle] = useState('');
  const [dlDate, setDlDate] = useState('');
  const [dlType, setDlType] = useState<WorkDeadlineType>('probation-end');
  const [dlNotes, setDlNotes] = useState('');
  const [dlSaving, setDlSaving] = useState(false);

  const loadDeadlines = useCallback(async (quiet = false) => {
    if (!user) {
      setDeadlines([]);
      setDeadlineError(null);
      setDeadlineLoading(false);
      return;
    }

    if (!quiet) {
      setDeadlineLoading(true);
    }
    setDeadlineError(null);

    const { data, error } = await supabase
      .from('work_deadlines' as any)
      .select('id, title, deadline_date, type, notes, completed, created_at')
      .eq('user_id', user.id)
      .order('deadline_date', { ascending: true })
      .order('created_at', { ascending: false });

    if (error) {
      console.error(error);
      setDeadlineError('Could not load your saved work deadlines right now.');
      setDeadlines([]);
    } else {
      setDeadlines(((data as WorkDeadline[]) || []).map((deadline) => ({
        ...deadline,
        type: (deadline.type || 'other') as WorkDeadlineType,
      })));
    }

    setDeadlineLoading(false);
  }, [user]);

  useEffect(() => {
    async function fetchCards() {
      setLoading(true);
      setLoadError(null);

      const { data, error } = await supabase
        .from('content_cards')
        .select('id, category, title, summary, body, icon, link_label, link_url, last_reviewed_at')
        .eq('section', 'employment-rights')
        .eq('is_active', true)
        .order('category')
        .order('sort_order');

      if (error) {
        console.error(error);
        setLoadError('Could not load employment rights right now.');
        setCards([]);
      } else {
        setCards((data as ContentCard[]) || []);
      }

      setLoading(false);
    }

    void fetchCards();
    void loadDeadlines();

    const handleRefresh = () => {
      void loadDeadlines(true);
    };

    window.addEventListener('adulting:data-changed', handleRefresh);
    return () => window.removeEventListener('adulting:data-changed', handleRefresh);
  }, [loadDeadlines]);

  const query = search.toLowerCase().trim();
  const deadlineSupport = DEADLINE_SUPPORT[dlType];

  const filtered = cards.filter(c =>
    !query ||
    c.title.toLowerCase().includes(query) ||
    c.summary.toLowerCase().includes(query) ||
    c.body.toLowerCase().includes(query)
  );

  // Group by category preserving order
  const grouped: { category: string; cards: ContentCard[] }[] = [];
  for (const card of filtered) {
    const last = grouped[grouped.length - 1];
    if (last && last.category === card.category) {
      last.cards.push(card);
    } else {
      grouped.push({ category: card.category, cards: [card] });
    }
  }
  const visibleRightsPacks = grouped
    .map((group) => {
      const pack = getWorkRightsPackByCategory(group.category);
      if (!pack) return null;
      return { group, pack };
    })
    .filter((value): value is { group: { category: string; cards: ContentCard[] }; pack: (typeof WORK_RIGHTS_PACKS)[number] } => Boolean(value));

  const pendingDeadlines = useMemo(
    () => deadlines.filter((deadline) => !deadline.completed).sort((a, b) => a.deadline_date.localeCompare(b.deadline_date)),
    [deadlines],
  );
  const completedDeadlines = useMemo(
    () => deadlines.filter((deadline) => deadline.completed).sort((a, b) => b.deadline_date.localeCompare(a.deadline_date)),
    [deadlines],
  );
  const dueSoonCount = pendingDeadlines.filter((deadline) => {
    const days = differenceInDays(parseISO(deadline.deadline_date), startOfDay(new Date()));
    return days >= 0 && days <= 14;
  }).length;

  const toggleSection = (section: keyof typeof openSections) => {
    setOpenSections((current) => ({
      ...current,
      [section]: !current[section],
    }));
  };

  const saveDeadline = async () => {
    if (!user || !dlTitle.trim() || !dlDate) return;

    setDlSaving(true);

    const { error } = await supabase.from('work_deadlines' as any).insert({
      user_id: user.id,
      title: dlTitle.trim(),
      deadline_date: dlDate,
      type: dlType,
      notes: dlNotes.trim() || null,
    });

    if (error) {
      console.error(error);
      toast.error('Could not save this work deadline right now.');
      setDlSaving(false);
      return;
    }

    setDlSaving(false);
    setShowAddDeadline(false);
    setDlTitle('');
    setDlDate('');
    setDlType('probation-end');
    setDlNotes('');
    toast.success('Work deadline saved.');
    notifySectionRefresh();
  };

  const toggleDeadline = async (deadline: WorkDeadline) => {
    const { error } = await supabase
      .from('work_deadlines' as any)
      .update({ completed: !deadline.completed })
      .eq('id', deadline.id);

    if (error) {
      console.error(error);
      toast.error('Could not update this work deadline right now.');
      return;
    }

    notifySectionRefresh();
  };

  if (loading) {
    return <AppLoadingState title="Loading employment rights" body="Pulling the latest cards and review dates." />;
  }

  if (loadError) {
    return (
      <AppErrorState
        title="Employment rights are unavailable"
        body={loadError}
        action={
          <AppButton tone="outline" onPress={() => window.location.reload()}>
            Try again
          </AppButton>
        }
      />
    );
  }

  return (
    <div className="space-y-4">
      <SectionAccordionCard
        emoji="📅"
        title="Deadlines"
        subtitle="Keep probation, review, renewal, flexible-working, and redundancy dates in the same place as your rights guidance."
        open={openSections.deadlines}
        onToggle={() => toggleSection('deadlines')}
      >
        <div className="space-y-4">
          <div className="flex items-start justify-between gap-3">
            <div />
            <AppButton size="sm" onPress={() => setShowAddDeadline((current) => !current)} disabled={!user}>
              <Plus size={12} />
              {showAddDeadline ? 'Cancel' : 'Add'}
            </AppButton>
          </div>

          {!user ? (
            <AppInlineAlert title="Sign in to save work deadlines">
              Deadline tracking saves per account once you are signed in.
            </AppInlineAlert>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-2">
                <MetricCard label="Pending" value={String(pendingDeadlines.length)} />
                <MetricCard label="Due in 14d" value={String(dueSoonCount)} tone={dueSoonCount > 0 ? 'warning' : 'default'} />
                <MetricCard label="Completed" value={String(completedDeadlines.length)} />
              </div>

              {deadlineError ? (
                <AppInlineAlert title="Deadline tracker issue" tone="warning">
                  {deadlineError}
                </AppInlineAlert>
              ) : null}

              {showAddDeadline ? (
                <AppCard backgroundColor="$mutedBackground" p="$sm">
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    <AppFieldGroup label="Deadline type" helper={deadlineSupport.helper}>
                      <SelectField
                        value={dlType}
                        onChange={(value) => setDlType(value as WorkDeadlineType)}
                        options={DEADLINE_TYPE_OPTIONS}
                      />
                    </AppFieldGroup>
                    <AppDateField label="Date" value={dlDate} onChange={setDlDate} />
                  </div>
                  <AppFieldGroup label="Deadline title">
                    <AppInput
                      tag="input"
                      value={dlTitle}
                      onChange={(event) => setDlTitle((event.target as HTMLInputElement).value)}
                      placeholder={deadlineSupport.placeholder}
                    />
                  </AppFieldGroup>
                  <AppFieldGroup label="Notes (optional)">
                    <AppTextArea
                      value={dlNotes}
                      onChange={(event) => setDlNotes((event.target as HTMLTextAreaElement).value)}
                      rows={3}
                      placeholder="Anything promised in writing, who the date belongs to, or what you need to chase next."
                    />
                  </AppFieldGroup>
                  <AppButton width="100%" onPress={saveDeadline} disabled={!dlTitle.trim() || !dlDate || dlSaving}>
                    {dlSaving ? 'Saving…' : 'Save work deadline'}
                  </AppButton>
                </AppCard>
              ) : null}

              {deadlineLoading ? (
                <AppLoadingState title="Loading work deadlines" body="Pulling your saved employment dates." />
              ) : pendingDeadlines.length === 0 && completedDeadlines.length === 0 ? (
                <AppEmptyState
                  title="No work deadlines yet"
                  body="Add the dates that are easiest to miss, like probation review, contract renewal, or a flexible-working response deadline."
                  action={
                    <AppButton tone="outline" onPress={() => setShowAddDeadline(true)}>
                      Add your first work deadline
                    </AppButton>
                  }
                />
              ) : (
                <div className="space-y-2">
                  {pendingDeadlines.map((deadline) => {
                    const badge = getDaysBadge(deadline.deadline_date);
                    const reminderWindow = getReminderWindow(deadline.deadline_date);
                    const support = DEADLINE_SUPPORT[deadline.type] || DEADLINE_SUPPORT.other;
                    return (
                      <div key={deadline.id} className="flex items-center gap-3 rounded-xl border border-border bg-background px-3 py-3">
                        <AppIconButton
                          type="button"
                          variant="outline"
                          onClick={() => void toggleDeadline(deadline)}
                          className="h-6 w-6 shrink-0 rounded-md border-2 border-border"
                        />
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <AppText fontSize="$sm" fontWeight="700" className="truncate">
                              {deadline.title}
                            </AppText>
                            <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                              {DEADLINE_TYPE_OPTIONS.find((option) => option.value === deadline.type)?.label || 'Work deadline'}
                            </span>
                          </div>
                          <AppText fontSize="$sm" color="$colorMuted">
                            {format(parseISO(deadline.deadline_date), 'd MMM yyyy')}
                          </AppText>
                          {reminderWindow ? (
                            <AppText fontSize="$sm" color="$colorMuted" mt="$xs">
                              {reminderWindow}
                            </AppText>
                          ) : null}
                          <AppText fontSize="$sm" color="$colorMuted" mt="$xs">
                            {support.summary}
                          </AppText>
                          {deadline.notes ? (
                            <AppText fontSize="$sm" color="$colorMuted" mt="$xs">
                              {deadline.notes}
                            </AppText>
                          ) : null}
                          <a
                            href={support.linkUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-primary"
                          >
                            <ExternalLink size={14} />
                            {support.linkLabel}
                          </a>
                        </div>
                        <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${badge.cls}`}>
                          {badge.text}
                        </span>
                      </div>
                    );
                  })}

                  {completedDeadlines.length > 0 ? (
                    <div className="space-y-2 border-t border-border pt-2">
                      {completedDeadlines.map((deadline) => (
                        <div key={deadline.id} className="flex items-center gap-3 rounded-xl border border-border bg-background px-3 py-3 opacity-60">
                          <AppIconButton
                            type="button"
                            variant="outline"
                            onClick={() => void toggleDeadline(deadline)}
                            className="h-6 w-6 shrink-0 rounded-md border-2 border-primary bg-primary/10"
                          >
                            <Check size={14} className="text-primary" />
                          </AppIconButton>
                          <div className="min-w-0 flex-1">
                            <AppText fontSize="$sm" color="$colorMuted" className="truncate line-through">
                              {deadline.title}
                            </AppText>
                            <AppText fontSize="$sm" color="$colorMuted" mt="$xs">
                              {format(parseISO(deadline.deadline_date), 'd MMM yyyy')}
                            </AppText>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          )}
        </div>
      </SectionAccordionCard>

      <SectionAccordionCard
        emoji="⚖️"
        title="Rights packs and guidance"
        subtitle="Quick routes, search, and detailed rights cards in one calmer section."
        open={openSections.rights}
        onToggle={() => toggleSection('rights')}
      >
        <div className="space-y-4">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <AppInput
              tag="input"
              type="text"
              value={search}
              onChange={(event) => setSearch((event.target as HTMLInputElement).value)}
              placeholder="Search rights…"
              className="pl-9"
            />
          </div>

          {visibleRightsPacks.length ? (
            <div className="overflow-x-auto pb-1">
              <div className="flex min-w-max gap-3 pr-1">
                {visibleRightsPacks.map(({ group, pack }) => (
                  <AppGuideCardButton
                    key={pack.id}
                    type="button"
                    minHeight={156}
                    className="w-[240px] shrink-0 text-left"
                    onPress={() => {
                      const firstCardId = group.cards[0]?.id;
                      if (firstCardId) {
                        setExpanded(firstCardId);
                      }
                      document.getElementById(`rights-pack-${pack.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }}
                  >
                    <div className="flex h-full flex-col justify-between gap-3">
                      <div className="space-y-3">
                        <div className="flex items-start gap-3">
                          <span className="text-2xl shrink-0">{pack.icon}</span>
                          <div className="min-w-0 flex-1">
                            <AppText color="$primaryForeground" fontSize="$xs" fontWeight="700" textTransform="uppercase" letterSpacing={0.8}>
                              {pack.eyebrow}
                            </AppText>
                            <AppText className="mt-1" color="$primaryForeground" fontSize="$sm" lineHeight="$sm" fontWeight="700">
                              {pack.title}
                            </AppText>
                            <AppText className="mt-2" color="$primaryForeground" opacity={0.82} fontSize="$sm" lineHeight={18}>
                              {pack.summary}
                            </AppText>
                          </div>
                        </div>
                      </div>
                      <div className="space-y-1">
                        <AppText color="$primaryForeground" opacity={0.68} fontSize="$xs" fontWeight="700" textTransform="uppercase" letterSpacing={0.8}>
                          Purpose
                        </AppText>
                        <AppText color="$primaryForeground" opacity={0.82} fontSize="$sm" lineHeight={18}>
                          {pack.purpose}
                        </AppText>
                      </div>
                    </div>
                  </AppGuideCardButton>
                ))}
              </div>
            </div>
          ) : null}

          {grouped.length === 0 ? (
            <AppEmptyState
              title={search ? `No results for "${search}"` : 'No rights cards available'}
              body={search ? 'Try a broader search term or clear the filter.' : 'Rights content will show here when the cards load for your account.'}
            />
          ) : null}

          {grouped.map((group) => {
            const pack = getWorkRightsPackByCategory(group.category);

            return (
              <div key={group.category} id={pack ? `rights-pack-${pack.id}` : undefined}>
                <div className="flex items-center gap-2 mb-2">
                  <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full ${CATEGORY_COLORS[group.category] || 'bg-muted text-muted-foreground'}`}>
                    {group.category}
                  </span>
                </div>
                <div className="space-y-2">
                  {group.cards.map((card) => {
                    const isOpen = expanded === card.id;
                    return (
                      <AppPanelButton
                        key={card.id}
                        type="button"
                        onClick={() => setExpanded(isOpen ? null : card.id)}
                        className="text-left"
                      >
                        <AppCard>
                          <AppCardBody>
                            <div className="flex items-start gap-2">
                              <span className="text-lg shrink-0">{card.icon}</span>
                              <div className="flex-1">
                                <AppText fontSize="$sm" fontWeight="700">{card.title}</AppText>
                                <AppText fontSize="$sm" color="$colorMuted" mt="$xs">{card.summary}</AppText>
                              </div>
                              <AppDisclosureGlyph open={isOpen} />
                            </div>
                            {isOpen ? (
                              <div className="pt-3 border-t border-border animate-fade-in">
                                <AppText fontSize="$sm">{card.body}</AppText>
                                {card.link_label && card.link_url ? (
                                  <a
                                    href={card.link_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1 mt-2 text-xs font-medium text-primary"
                                    onClick={(event) => event.stopPropagation()}
                                  >
                                    <ExternalLink size={12} /> {card.link_label}
                                  </a>
                                ) : null}
                                <AppText fontSize="$sm" color="$colorMuted" mt="$sm">
                                  Last reviewed {format(parseISO(card.last_reviewed_at), 'MMMM yyyy')}
                                </AppText>
                              </div>
                            ) : null}
                          </AppCardBody>
                        </AppCard>
                      </AppPanelButton>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </SectionAccordionCard>

      <SectionAccordionCard
        emoji="🧭"
        title="Help and backup"
        subtitle="Keep the external routes visible without leaving them mixed into the rights cards."
        open={openSections.help}
        onToggle={() => toggleSection('help')}
      >
        <AppCard>
          <AppCardBody>
            <AppText fontSize="$sm" fontWeight="600" color="$colorMuted" textTransform="uppercase" letterSpacing={1.2}>
              Need help?
            </AppText>
            <a
              href="https://www.citizensadvice.org.uk"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 py-2 transition-all active:scale-[0.98]"
            >
              <ExternalLink size={14} className="text-primary shrink-0" />
              <span className="text-sm font-medium text-foreground">Find a free employment solicitor → citizensadvice.org.uk</span>
            </a>
            <a
              href="tel:03001231100"
              className="flex items-center gap-3 py-2 border-t border-border transition-all active:scale-[0.98]"
            >
              <Phone size={14} className="text-primary shrink-0" />
              <span className="text-sm font-medium text-foreground">ACAS helpline → 0300 123 1100</span>
            </a>
          </AppCardBody>
        </AppCard>
      </SectionAccordionCard>
    </div>
  );
}
